import { useAutoAnimate } from '@formkit/auto-animate/react';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { Industry } from '../../../extViewmodels';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import { ILocationState } from '../../../models';
import { useErrorMessages, useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import {
	invalidateAllTemplatesByTypeQuery,
	invalidateGetNewsletterTemplatesByKeyword,
	useGetNewsletterTemplatesByKeyword,
	useTemplatesByType,
} from '../../../queries';
import { invalidateTemplateKeywords, useTemplateKeywords } from '../../../queries/Templates/useEmailTemplateKeywords';
import {
	invalidateAllTemplateSystemQuery,
	useGetTemplatesByIndustry,
} from '../../../queries/Templates/useGetTemplatesByIndustry';
import { asApiError, ITemplate, TemplateType } from '../../../viewmodels/AppViewModels';
import { HtmlNewsletterViewModel } from '../../../viewmodels/HtmlNewletterViewModels';
import { Button } from '../../components-v2/Button';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { HtmlNewsletterTemplateCard } from '../../components/templateCards/HtmlNewsletterTemplateCard';
import { CampaignBrowser } from '../CampaignBrowser';
import { HtmlNewsletterTemplatesHeader } from '../HtmlNewsletterTemplates/presentation';
import { TemplateFolderTabs } from './TemplateFolderTabs';
import { useTemplateFolders } from './hooks/useTemplateFolders';
import { styleSheet } from './styles';
import { filterLevDraftTemplates } from './utils';

export const HtmlNewslettersCampaignCards = observer(() => {
	const userSession = useUserSession();
	const [industry, setIndustry] = useState<Industry>(userSession.account.additionalInfo.industry);
	const [search, setSearch] = useState<string>(null);
	const [templateListRef] = useAutoAnimate<HTMLUListElement>();

	// Handle industry changes
	const handleIndustryChange = React.useCallback((newIndustry: Industry) => {
		setIndustry(newIndustry);
	}, []);

	const history = useHistory();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('HtmlNewsletterTemplates');
	const [isCreatingNewTemplate, setIsCreatingNewTemplate] = React.useState(false);
	const isCustomerImpersonation = userSession?.isCustomerImpersonation;

	const debouncedKeywordsSearchValue = useDebounceValue(search);

	// Industry templates query - gets system/industry templates but NOT user templates
	const industryTemplates = useGetTemplatesByIndustry({
		enabled: !debouncedKeywordsSearchValue, // Disable when searching
		impersonationContext: userSession.impersonationContext,
		industry,
		types: [TemplateType.HtmlNewsletter],
		meta: {
			errorLogging: {
				category: 'HtmlNewslettersCampaignCards',
				action: 'HtmlNewslettersCampaignCardsGetIndustryTemplates-Error',
			},
		},
	});

	// User templates query - gets user's custom templates
	const userTemplates = useTemplatesByType({
		enabled: !debouncedKeywordsSearchValue, // Disable when searching
		impersonationContext: userSession.impersonationContext,
		type: TemplateType.HtmlNewsletter,
		includeNonUserTemplates: true,
	});

	// Fetch all keywords for folder extraction
	const keywordsQuery = useTemplateKeywords({
		industry,
		enabled: !debouncedKeywordsSearchValue, // Disable during search
	});

	// Combine and deduplicate templates for non-search view
	const templatesMemo = useMemo(() => {
		if (!industryTemplates?.data) {
			return [];
		}

		const combinedTemplates = [...industryTemplates.data];

		// Add user templates if available, ensuring no duplicates by ID
		if (userTemplates?.data?.pages) {
			const userTemplatesList = userTemplates.data.pages.flatMap(page => page.values);
			const existingIds = new Set(combinedTemplates.map(t => t.id));

			userTemplatesList.forEach(userTemplate => {
				if (!existingIds.has(userTemplate.id)) {
					combinedTemplates.push(userTemplate);
				}
			});
		}

		return combinedTemplates;
	}, [industryTemplates?.data, userTemplates?.data]);

	const onCreateNewClicked = React.useCallback(async () => {
		logger.logInput('CreateNewTemplate', 'Click');
		try {
			setIsCreatingNewTemplate(true);
			const template = await HtmlNewsletterViewModel.createDefaultBlankTemplate(userSession, {
				name: 'Untitled Newsletter',
			});
			const locationState: ILocationState<any, ITemplate> = { model: template };
			setIsCreatingNewTemplate(false);
			history?.push({
				pathname: '/campaigns/htmlnewsletter/new',
				state: locationState,
			});
		} catch (err) {
			const error = asApiError(err);
			logger.logApiError('CreateTemplate-Error', error);
			errorMessages?.pushApiError(error);
			setIsCreatingNewTemplate(false);
		}
		invalidateAllTemplateSystemQuery();
		invalidateTemplateKeywords({ industry });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [industry]);

	const filteredNewslettersQuery = useGetNewsletterTemplatesByKeyword({
		enabled: !!debouncedKeywordsSearchValue,
		industry,
		impersonationContext: userSession.impersonationContext,
		meta: {
			errorLogging: {
				category: 'HtmlNewslettersCampaignCards',
				action: 'HtmlNewslettersCampaignCardsFilterNewsletterByKeyword-Error',
			},
		},
		search: debouncedKeywordsSearchValue,
	});

	// Memoize filtered templates for performance
	const filteredTemplates = React.useMemo(
		() => filterLevDraftTemplates(templatesMemo || [], isCustomerImpersonation),
		[templatesMemo, isCustomerImpersonation]
	);

	const filteredSearchTemplates = React.useMemo(
		() => filterLevDraftTemplates(filteredNewslettersQuery?.data || [], isCustomerImpersonation),
		[filteredNewslettersQuery?.data, isCustomerImpersonation]
	);

	// Folder filtering for HTML Newsletter templates
	const {
		folders,
		selectedFolder,
		setSelectedFolder,
		filteredTemplates: folderFilteredTemplates,
	} = useTemplateFolders({
		templates: filteredTemplates,
		allKeywords: keywordsQuery.data ?? [],
	});

	let gridView: 'showCards' | 'showFilteredCards' | 'showLoading' = 'showLoading';
	// When searching: use filtered results that include user templates automatically
	if (!!search && !filteredNewslettersQuery.isLoading) {
		gridView = 'showFilteredCards';
	}
	// When not searching: show combined industry + user templates
	// IMPORTANT: Wait for BOTH industry AND user templates to be ready before showing
	else if (
		!search &&
		!industryTemplates.isLoading &&
		!userTemplates.isLoading &&
		industryTemplates.data &&
		userTemplates.data
	) {
		gridView = 'showCards';
	}

	const renderHtmlNewsletterRightActions = React.useMemo(() => {
		return (
			<Button
				onClick={onCreateNewClicked}
				disabled={isCreatingNewTemplate || industryTemplates.isFetching || userTemplates.isFetching}
				title='Build HTML Template'
			>
				Build HTML Template
			</Button>
		);
	}, [isCreatingNewTemplate, industryTemplates.isFetching, userTemplates.isFetching, onCreateNewClicked]);

	return (
		<CampaignBrowser
			categories={[]}
			category=''
			searchPlaceholder='Search all newsletters'
			industry={industry}
			onIndustryChange={handleIndustryChange}
			search={search}
			onSearchChange={setSearch}
			renderRightActions={renderHtmlNewsletterRightActions}
		>
			<HtmlNewsletterTemplatesHeader />

			{/* Folder tabs - hidden during search */}
			{!search && (
				<TemplateFolderTabs folders={folders} selectedFolder={selectedFolder} onFolderSelect={setSelectedFolder} />
			)}

			<div key={`${industry}-templates`} className={css(styleSheet.templatesContainer)}>
				<ul
					key={`${industry}-${gridView}-${selectedFolder ?? 'all'}`} // Force reset when industry, view, or folder changes
					className={css(styleSheet.templates)}
					ref={templateListRef}
				>
					{gridView === 'showCards' &&
						folderFilteredTemplates.map(template => {
							return (
								<li key={template.id}>
									<HtmlNewsletterTemplateCard
										industry={industry}
										key={template.id}
										template={template}
										onUpdate={() => {
											invalidateAllTemplatesByTypeQuery();
											invalidateAllTemplateSystemQuery();
											// Also invalidate any cached search results
											invalidateGetNewsletterTemplatesByKeyword({
												industry,
												impersonationContext: userSession.impersonationContext,
											});
										}}
									/>
								</li>
							);
						})}
					{gridView === 'showFilteredCards' &&
						filteredSearchTemplates.map(template => (
							<li key={template.id}>
								<HtmlNewsletterTemplateCard
									industry={industry}
									key={template.id}
									template={template}
									onUpdate={() => {
										invalidateGetNewsletterTemplatesByKeyword({
											industry,
											impersonationContext: userSession.impersonationContext,
											search: debouncedKeywordsSearchValue,
										});
										// Also invalidate the non-search queries in case user clears search
										invalidateAllTemplatesByTypeQuery();
										invalidateAllTemplateSystemQuery();
									}}
								/>
							</li>
						))}
					{gridView === 'showLoading' && (
						<li className={css(styleSheet.loadingContainer)} key='loading'>
							<LoadingSpinner type='large' />
						</li>
					)}
				</ul>
			</div>
			{!search && industryTemplates.isSuccess && !userTemplates.isPending && userTemplates.hasNextPage && (
				<Waypoint bottomOffset='-200px' onEnter={() => userTemplates.fetchNextPage()} />
			)}
		</CampaignBrowser>
	);
});
